import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import logo from "../assets/images/logo_b4t.jpeg";
import Sidebar from "./Sidebar";
import usericon from "../assets/images/default-avatar.png";
import { getDetails } from "../Redux/Action/SubAdmin";
import "../styles/ModernLayout.css";

export default function Layout(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Route labels for header title
  const routeLabels = {
    '/dashboard': 'Dashboard',
    '/booking-request': 'Booking Requests',
    '/booking': 'Bookings',
    '/carer-availability': 'Carer Availability',
    '/users': 'User Management',
    '/carer': 'Carer Management',
    '/transaction': 'Transactions',
    '/reports': 'Reports',
    '/settings': 'Admin Settings',
    '/add-sub-admin': 'Add Sub-Admin',
    '/sub-admin-management': 'Sub-Admin Management',
    '/notification': 'Notifications',
    '/offer': 'Offers & Promotions',
    '/contentmanagement': 'Content Management',
    '/faq-management': 'FAQ Management',
    '/termandpolicies': 'Terms & Policies',
    '/app-config': 'App Configuration',
    '/wifi-connection': 'WiFi Connection',
    '/otp-exceptions': 'OTP Exceptions',
    '/custom-message': 'Custom Messages'
  };

  // Get current page title
  const getPageTitle = () => {
    const path = location.pathname;
    // Check for exact match
    if (routeLabels[path]) return routeLabels[path];
    
    // Check for partial matches (e.g., /users/123)
    const matchingKey = Object.keys(routeLabels).find(key => 
      path.startsWith(key) && key !== '/'
    );
    return matchingKey ? routeLabels[matchingKey] : 'Dashboard';
  };

  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';

  // Get admin details from Redux
  const admindetails = useSelector((state) => state.SubAdmin.admindetails);

  useEffect(() => {
    dispatch(getDetails());
  }, [dispatch]);

  // Close dropdowns when clicking outside
  const closeDropdowns = () => {
    setQuickActionsOpen(false);
    setUserMenuOpen(false);
  };

  // Get user image - use uploaded image if available, fallback to default
  const getUserImage = () => {
    if (admindetails?.image) {
      // If the image is a full URL, use it directly
      if (admindetails.image.startsWith('http')) {
        return admindetails.image;
      }
      // If it's a relative path, construct the full URL
      return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${admindetails.image}`;
    }
    return usericon; // Fallback to default image
  };

  // Get user name
  const getUserName = () => {
    return admindetails?.name || admindetails?.username || 'Admin';
  };

  // Get user role
  const getUserRole = () => {
    return admindetails?.role || admindetails?.user_type || 'Administrator';
  };

  return (
    <div className="min-h-screen bg-slate-50" onClick={closeDropdowns}>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 
        gradient-emerald shadow-emerald-lg
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 flex flex-col
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-6 border-b border-emerald-600/30 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src={logo} 
              alt="Logo" 
              className="h-10 w-10 rounded-lg object-cover ring-2 ring-white/20 shadow-lg" 
            />
            <div className="text-white">
              <h2 className="text-lg font-bold">B4T Admin</h2>
              <p className="text-xs text-emerald-200">Management Panel</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:text-emerald-200 transition-colors p-1 rounded-md hover:bg-white/10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sidebar Navigation - Scrollable */}
        <div className="flex-1 overflow-y-auto py-4 sidebar-scrollbar min-h-0">
          <Sidebar />
        </div>

        {/* Sidebar Footer - Sticky at bottom */}
        <div className="p-4 border-t border-emerald-600/30 flex-shrink-0 bg-gradient-to-r from-emerald-800 to-emerald-900">
          <div className="text-center text-emerald-200 text-xs">
            © {new Date().getFullYear()} Buddy4Travel
          </div>
        </div>
      </aside>

      {/* Header */}
      <header className="fixed top-0 right-0 left-0 lg:left-64 z-30 h-16 bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between h-full px-6">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors focus-ring"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Header Title - Hidden on mobile */}
          <div className="hidden lg:block">
            <h1 className="text-xl font-semibold text-gray-800">{getPageTitle()}</h1>
            {isDashboard && <p className="text-sm text-gray-500">Welcome back, Admin</p>}
          </div>

          {/* Header Actions */}
          <div className="flex items-center space-x-4">
            {/* Quick Actions Dropdown */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickActionsOpen(!quickActionsOpen);
                  setUserMenuOpen(false);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors focus-ring"
              >
                <i className="fas fa-bolt text-emerald-600"></i>
                <span className="hidden md:inline">Quick Actions</span>
                <svg className={`w-4 h-4 transition-transform ${quickActionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Quick Actions Dropdown */}
              {quickActionsOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-emerald border border-gray-200 z-50" onClick={(e) => e.stopPropagation()}>
                  <div className="py-2">
                    <button 
                      onClick={() => {
                        window.location.href = '/add-sub-admin';
                        setQuickActionsOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <i className="fas fa-plus w-4 mr-3 text-emerald-600"></i>
                      Add New User
                    </button>
                    <button 
                      onClick={() => {
                        window.location.href = '/report';
                        setQuickActionsOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <i className="fas fa-chart-bar w-4 mr-3 text-emerald-600"></i>
                      View Reports
                    </button>
                    <button 
                      onClick={() => {
                        window.location.href = '/transaction';
                        setQuickActionsOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <i className="fas fa-download w-4 mr-3 text-emerald-600"></i>
                      Export Data
                    </button>
                    <hr className="my-2 border-gray-200" />
                    <button 
                      onClick={() => {
                        window.location.href = '/settings';
                        setQuickActionsOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <i className="fas fa-cog w-4 mr-3 text-emerald-600"></i>
                      Settings
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu - More Compact */}
            <div className="relative">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setUserMenuOpen(!userMenuOpen);
                  setQuickActionsOpen(false);
                }}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors focus-ring"
              >
                <img 
                  src={getUserImage()} 
                  alt={getUserName()} 
                  className="h-8 w-8 rounded-full object-cover ring-2 ring-emerald-500 shadow-sm" 
                  onError={(e) => {
                    e.target.src = usericon; // Fallback to default image on error
                  }}
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-900 leading-tight">
                    {admindetails ? getUserName() : 'Loading...'}
                  </p>
                </div>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* User Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-emerald border border-gray-200 z-50" onClick={(e) => e.stopPropagation()}>
                  <div className="py-2">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{getUserName()}</p>
                      <p className="text-xs text-gray-500">{getUserRole()}</p>
                    </div>
                    <button 
                      onClick={() => {
                        window.location.href = '/settings';
                        setUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profile
                    </button>
                    <hr className="my-2 border-gray-200" />
                    <button
                      onClick={() => {
                        localStorage.clear();
                        window.location.href = "/";
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="w-full min-h-full p-4">
          {props.children}
        </div>
      </main>
    </div>
  );
}
